import { useState, useMemo, useEffect, useCallback } from 'react'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { useStripe } from '@stripe/react-stripe-js'
import { toast } from 'react-toastify'

const log = new Logger('Billing')

/** @returns BillingViewModel */
const BillingViewModel = () => {
    
    const remoteConfig = useWireValue(store.remoteConfig)
    
    // @todo suppressed lint for this since it's a feature flag; remove once deployed to prod
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const stripe = remoteConfig.stripe.enabled ? useStripe() : null
    
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState()
    const [endDate, setEndDate] = useState()
    const [stripeCustomer, setStripeCustomer] = useState()
    /** @type {[StripeCard[], Dispatch<SetStateAction<StripeCard[]>>]} */
    const [stripeCards, setStripeCards] = useState([])
    const [deleteCardModalOpen, setDeleteCardModalOpen] = useState(false)
    const [deleteCardModalCard, setDeleteCardModalCard] = useState(null)
    
    const [addPaymentCardDialogOpen, setAddPaymentCardDialogOpen] = useWireState(store.addPaymentCardDialogOpen)
    
    /* @type ReadonlyStateWire<User> */
    const user = useWireValue(store.user)
    const credits = useWireValue(store.credits)
    const billingInfo = useWireValue(store.billingInfo)
    
    const planDetails = useMemo(() => (
        user.roles
            ?.sort((a, b) => {
                if (a.createdAt > b.createdAt) return 1
                if (a.createdAt < b.createdAt) return -1
                return 0
            })
            ?.map(it => it.name)
            ?.join(', ') ?? '(Unknown)'
    ), [user])
    
    const deleteSavedCard = useCallback(async () => {
        
        try {
            
            await actions.deleteCreditCard(deleteCardModalCard.id)
            
            setStripeCards(prev => prev.filter(it => it.id !== deleteCardModalCard.id))
            
            toast.success('Card deleted')
            
        } catch (e) {
            
            console.error('deleteSavedCard', e)
            toast.error('Failed to delete card')
            
        }
        
    }, [deleteCardModalCard])
    
    const onRemoveCardClick = card => {
        
        setDeleteCardModalCard(card)
        setDeleteCardModalOpen(true)
        
    }
    
    useEffect(() => {
        
        if (!stripe || stripeCustomer)
            return
        
        setLoading(true)
        
        actions.fetchStripeCustomer()
            .then(res => {
                log.d('res.data', res)
                setStripeCustomer(res?.customer)
                setStripeCards(res?.cards?.data || [])
            })
            .catch(e => log.e('fetchStripeCustomer', e))
            .finally(() => setLoading(false))
        
    }, [stripe, stripeCustomer])
    
    // @todo @deprecated - likely not used anymore, will remove after Stripe is live
    /* useEffect(() => {
        
        if (!stripe) return
        
        // Retrieve the "setup_intent_client_secret" query parameter appended to
        // your return_url by Stripe.js
        const clientSecret = new URLSearchParams(window.location.search).get('setup_intent_client_secret')
        
        // If Stripe redirected back here, open the "add card" dialog again to show confirmation
        if (clientSecret?.length) setAddPaymentCardDialogOpen(true)
        
    }, [stripe]) */
    
    return {
        
        // State
        loading,
        setLoading,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        stripeCards,
        deleteCardModalOpen,
        setDeleteCardModalOpen,
        deleteCardModalCard,
        setDeleteCardModalCard,
        
        // Global State
        user,
        credits,
        remoteConfig,
        billingInfo,
        addPaymentCardDialogOpen,
        setAddPaymentCardDialogOpen,
        
        // Memos
        planDetails,
        
        // Methods
        deleteSavedCard,
        onRemoveCardClick,
        
    }
    
}

export default BillingViewModel
